import { Button } from "@workspace/web-ui/components/button";
import { useState, Fragment } from "react";
import { PrescriptionModal } from "../modals/prescription-modal";
import { PatientMedicalPlan } from "../../api/types";
import EmptyMedicalCard from "../empty-medical-cards";
import allergiesImage from "~/assets/images/dashboard/allergies-medicals.svg";
import healthCondition from "~/assets/images/dashboard/health-condition.svg";
import { X } from "lucide-react";
import { MultiStepDialog } from "../../../../components/multi-step-dialog";
import { Input } from "@workspace/web-ui/components/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@workspace/web-ui/components/select";
import { Textarea } from "@workspace/web-ui/components/textarea";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@workspace/web-ui/components/alert-dialog";
import { usePrescription } from "../../hooks/use-prescription";
import { usePrescriptionModalStore } from "../../store/prescription-modal-store";
import { getRouteApi } from "@tanstack/react-router";

function formatFullDate(dateString: string): string | null {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error("Error formatting date:", error);
    return null;
  }
}

interface MedicalPlanDetailsProps {
  patient: PatientMedicalPlan | null;
  userId: string;
  encounterId?: string;
}

const MedicalPlanDetails = ({ patient, userId, encounterId }: MedicalPlanDetailsProps) => {
  const { openPrescriptionModal, setOpenPrescriptionModal} = usePrescriptionModalStore()
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [medication, setMedication] = useState(patient?.program_details.plan.medication.name || "");
  const [newMedication, setNewMedication] = useState(medication);
  const [justification, setJustification] = useState("");
  const [dosage, setDosage] = useState("");
  const [price, setPrice] = useState("");
  const [duration, setDuration] = useState("");
  const [instructions, setInstructions] = useState("");
  const [refills, setRefills] = useState("");
  const [, setMedicationReviewed] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
  const { program } = routeApi.useParams();

  const medicationOptions = [
    "Compounded Semaglutide",
    "Compounded Tirzepatide",
    "Zepbound (Branded Tirzepatide)",
  ];

  const { handleSubmit } = usePrescription(encounterId || "");

  if (openPrescriptionModal) {
    return <PrescriptionModal
      open={openPrescriptionModal}
      patientId={userId}
      onOpenChange={setOpenPrescriptionModal}
    />
  }

  return (
    <div className="space-y-6">
      {/* Current Medical Plan Card */}
      {program === "weight-loss" && (
      <div className="rounded-lg border shadow w-full bg-white">
        <div className="p-4 border-b">
          <h2 className="text-lg font-medium">Current Medical Plan</h2>
        </div>
        {/* Program Details */}
        <div className="p-4 border-b">
          <h3 className="font-medium mb-2">Program Details</h3>
          <div className="space-y-3 text-sm">
            <div>
              <span className="font-medium">Program: </span>
              {patient?.program_details.program}
            </div>
            <div>
              <span className="font-medium">Start Date: </span>
              {patient?.program_details.start_date && formatFullDate(patient?.program_details.start_date)}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* MultiStepDialog for Review/Edit Medication */}
      <MultiStepDialog
        open={openReviewModal}
        onOpenChange={setOpenReviewModal}
        initialData={{
          medication: medication,
          newMedication: newMedication,
          reason: justification,
          dosage: dosage,
          refills: refills,
          price: price,
          duration: duration,
          instructions: instructions,
          vitamin: "",
        }}
        steps={[
          {
            title: `Review" medication - ${patient?.patient_summary.full_name || "Patient"}`,
            content: (formData, setFormData) => (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs mb-1">Current Medication:</label>
                  <div className="bg-gray-100 rounded px-3 py-2 text-sm">{formData.medication}</div>
                </div>
                <div>
                  <label className="block text-xs mb-1">New Medication</label>
                  <Select
                    value={formData.newMedication}
                    onValueChange={val => setFormData(f => ({ ...f, newMedication: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select medication" />
                    </SelectTrigger>
                    <SelectContent>
                      {medicationOptions.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs mb-1">Medical Justification:</label>
                  <Textarea
                    value={formData.reason}
                    onChange={e => setFormData(f => ({ ...f, reason: e.target.value }))}
                    placeholder="Enter medical reason for medication change"
                    maxLength={200}
                  />
                  <div className="text-xs text-right">{formData.reason?.length || 0}/200</div>
                </div>
              </div>
            ),
            validate: (formData) => !!formData.newMedication && !!formData.reason,
          },
          {
            title: `Review medication - ${patient?.patient_summary.full_name || "Patient"}`,
            content: (formData, setFormData) => (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs mb-1">Dosage</label>
                  <Input
                    value={formData.dosage}
                    onChange={e => setFormData(f => ({ ...f, dosage: e.target.value }))}
                    placeholder="e.g. 0.25mg weekly injection"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs mb-1">Price</label>
                    <Input
                      value={formData.price}
                      onChange={e => setFormData(f => ({ ...f, price: e.target.value }))}
                      placeholder="$"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs mb-1">Duration</label>
                    <Input
                      value={formData.duration}
                      onChange={e => setFormData(f => ({ ...f, duration: e.target.value }))}
                      placeholder="e.g. 1 Month"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs mb-1">Instructions for medication</label>
                  <Textarea
                    value={formData.instructions}
                    onChange={e => setFormData(f => ({ ...f, instructions: e.target.value }))}
                    placeholder="Provide instructions for the patient on how to take their medication."
                  />
                </div>
              </div>
            ),
            validate: (formData) =>
              !!formData.dosage && !!formData.price && !!formData.duration && !!formData.instructions,
          },
        ]}
        onComplete={async (formData) => {
          try {
            await handleSubmit({
              medication: formData.newMedication,
              dosage: formData.dosage,
              duration: formData.duration,
              cost: Number(formData.price) || 0,
              note: formData.instructions,
              refills: Number(formData.refills) || 0,
              vitamin: { name: formData.newMedication === "Zepbound (Branded Tirzepatide)" ? "" : formData.vitamin },
              reason: formData.reason,
              substitute: true,
              patient_id: userId ?? ""
            });
            setMedication(formData.newMedication);
            setMedicationReviewed(true);
            setOpenReviewModal(false);
          } catch (error) {
            // Error is already handled by the hook (toast shown)
          }
        }}
      />

      {/* Conditions & Allergies Card */}
      <div className="rounded-lg border shadow w-full bg-white">
        <div className="p-4 border-b">
          <h2 className="text-lg font-medium">Conditions & allergies</h2>
        </div>
        
        {/* Allergies */}
        <div className="p-4 border-b bg-white">
          <h3 className="font-medium mb-2">Allergies</h3>
          <div className="space-y-3">
            {patient?.conditions_and_allergies.allergies && patient?.conditions_and_allergies.allergies?.length > 0 ? <>
              {patient?.conditions_and_allergies.allergies.map((allergy, index) => (
                <div key={index} className="flex flex-col bg-[#6E23C90D] border-l-4 border-l-[#6E23C9] py-3 px-5 rounded-r-[8px] items-start">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-2 ${allergy.severity === 'Severe' ? 'bg-red-500' :
                      allergy.severity === 'Moderate' ? 'bg-yellow-500' : 'bg-green-500'
                      }`}></div>
                    <span className="text-[#1A202C] font-medium">{allergy.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[14px]">
                    <div className="text-gray-500">Severity:</div>
                    <span className="">{allergy.severity}</span>
                  </div>
                </div>
              ))}
            </> : (<EmptyMedicalCard
              title="Allergies"
              image={allergiesImage}
              description=""
              altImg={"allergies"}
              message={`This patient has no recorded allergies`}
            />)}
          </div>
        </div>

        {/* Health Conditions */}
        <div className="p-4">
          <h3 className="font-medium mb-2">Health Conditions</h3>
          <div className="grid grid-cols-1 items-center gap-4">
            {patient?.conditions_and_allergies?.health_conditions && patient?.conditions_and_allergies?.health_conditions?.length > 0 ? <>
              <ul className="grid grid-cols-1 gap-5">
          {patient?.conditions_and_allergies?.health_conditions.map((condition, index) => {

            if (condition.name === "Diabetes") return (
              <Fragment key={index}>
                <li
                  key={index}
                  className="px-5 py-3 border-l-[4px] items-start justify-between border-[#4272DD] shadow-sm flex bg-[#E3F0FF]"
                >
                  <div className="flex flex-col text-left space-y-1">
                    <h3 className="font-medium mb-3">{condition.name}</h3>
                    <p className="text-sm">Status: <span className="font-medium">Patient is diabetic</span></p>
                    <p className="text-sm">Medication: <span className="font-medium">{condition.on_insulin ? `Insulin${condition.other_medication && !condition.other_medication.toLowerCase().includes("no") && !condition.other_medication.toLowerCase().includes("non") && !condition.other_medication.toLowerCase().includes("none") && !condition.other_medication.toLowerCase().includes("n/a") && !condition.other_medication.toLowerCase().includes("null") && !condition.other_medication.toLowerCase().includes("undefined") ? `,${condition.other_medication}` : ""}` : condition.other_medication ? condition.other_medication : "--"} </span></p>
                    {condition.last_hba1c_value && <p className="text-sm">HbA1c: <span className="font-medium">{condition.last_hba1c_value ? condition.last_hba1c_value : "--"}</span></p>}
                  </div>
                </li>
              </Fragment>
            );

            return (
              <Fragment key={index}>
                <li
                  key={index}
                  className="px-5 py-3 border-l-[4px] items-start justify-between border-[#FA3131] shadow-sm flex bg-[#FFE3E380]"
                >
                  <div className="flex flex-col text-left space-y-1">
                    <h3 className="font-medium mb-3">{condition.name}</h3>
                    <p className="text-sm">Diagnosed: <span className="font-medium">{new Date(condition.date_diagnosed).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span></p>
                    <p className="text-sm">Status: <span className="font-medium">{condition.status}</span></p>
                  </div>
                </li>
              </Fragment>
            )
          })}
        </ul>
            </> : (<EmptyMedicalCard
              title="Health Conditions"
              image={healthCondition}
              description=""
              altImg={"Health Conditions"}
              message={`This patient has no recorded health conditions`}
            />)}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={openDeleteDialog} onOpenChange={setOpenDeleteDialog}>
        <AlertDialogContent>
            <AlertDialogCancel asChild>
              <Button variant="ghost" className="absolute right-4 top-4 h-6 w-6 p-0">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </AlertDialogCancel>
            <div className="mt-10 text-center w-full flex items-center border-b">
              <p className="w-full mx-auto text-[#2D2F35] font-medium text-[20px] md:w-[70%]">Are you sure you want to delete this medication? This action cannot be undone.</p>
            </div>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline" className="rounded-[10px] h-[46px]">Cancel</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                 className="rounded-[10px] h-[46px]"
                onClick={() => {
                  setMedicationReviewed(false);
                  setNewMedication("");
                  setJustification("");
                  setDosage("");
                  setPrice("");
                  setDuration("");
                  setRefills("");
                  setInstructions("");
                  setOpenDeleteDialog(false);
                }}
              >
                Confirm
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MedicalPlanDetails;